import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Check,
  Clock,
  Zap,
  Square,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { dreamsApi, Dream } from '../lib/api';
import { mockDreams, useMockMode } from '../lib/mockData';

export default function DreamsLibraryPage() {
  const navigate = useNavigate();

  const [dreams, setDreams] = useState<Dream[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'realized'>('all');
  const [activeDreamIds, setActiveDreamIds] = useState<Set<string>>(new Set());
  const isMock = useMockMode();

  useEffect(() => {
    loadDreams();
  }, []);

  // Init active dreams from data
  useEffect(() => {
    const activeIds = new Set(
      dreams.filter((d) => d.status === 'active').map((d) => d.id)
    );
    setActiveDreamIds(activeIds);
  }, [dreams]);

  const loadDreams = async () => {
    setIsLoading(true);
    if (isMock) {
      await new Promise((r) => setTimeout(r, 400));
      setDreams(mockDreams);
      setIsLoading(false);
      return;
    }
    try {
      const result = await dreamsApi.list();
      setDreams(result.dreams);
    } catch (err) {
      console.error('Failed to load dreams:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = (dreamId: string) => {
    setActiveDreamIds((prev) => {
      const next = new Set(prev);
      if (next.has(dreamId)) {
        next.delete(dreamId);
      } else {
        next.add(dreamId);
      }
      return next;
    });
  };

  const handleMarkRealized = async (dreamId: string) => {
    if (isMock) {
      setDreams((prev) =>
        prev.map((d) => (d.id === dreamId ? { ...d, status: 'realized' as const } : d))
      );
      return;
    }
    try {
      await dreamsApi.update(dreamId, { status: 'realized' });
      loadDreams();
    } catch (err) {
      console.error('Failed to mark realized:', err);
    }
  };

  const filteredDreams = dreams.filter((dream) => {
    if (filter === 'all') return true;
    if (filter === 'active') return dream.status === 'active' || dream.status === 'inactive';
    if (filter === 'realized') return dream.status === 'realized';
    return true;
  });

  const hasActiveDreams = activeDreamIds.size > 0;

  return (
    <div className="min-h-screen flex flex-col safe-area-inset relative">
      {/* Carbon base */}
      <div className="fixed inset-0 bg-system-carbon-500 -z-20" />
      {/* Subtle light on top-left */}
      <div className="fixed inset-0 -z-10" style={{
        background: 'radial-gradient(ellipse at 10% -10%, rgba(254,254,254,0.04) 0%, transparent 50%)',
      }} />

      {/* Header */}
      <header className="flex items-center justify-between px-6 pt-4 pb-2">
        <h1 className="text-2xl font-display font-bold text-white">Mes reves</h1>
        {/* Start/Stop global */}
        {hasActiveDreams && (
          <button
            onClick={() => {
              // Navigate to viewer of first active dream
              const firstActive = [...activeDreamIds][0];
              if (firstActive) navigate(`/dream/${firstActive}`);
            }}
            className="btn-glass flex items-center gap-2 px-4 py-2 text-sm"
          >
            <Zap className="w-4 h-4" />
            <span>Demarrer</span>
          </button>
        )}
      </header>

      {/* Filter tabs */}
      <div className="flex gap-2 px-5 mb-4 mt-1">
        {[
          { key: 'all', label: 'Tous' },
          { key: 'active', label: 'En cours' },
          { key: 'realized', label: 'Realises' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key as typeof filter)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              filter === tab.key
                ? 'text-white/90 border border-white/20'
                : 'text-white/35 border border-transparent'
            }`}
            style={{
              background: filter === tab.key
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(255,255,255,0.03)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dreams list */}
      <main className="flex-1 px-5 pb-28 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-white/20" />
          </div>
        ) : filteredDreams.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl glass-card flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white/15" />
            </div>
            <p className="text-ivory-muted">
              {filter === 'realized'
                ? 'Aucun reve realise pour le moment'
                : 'Aucun reve cree'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDreams.map((dream, index) => {
              const isActive = activeDreamIds.has(dream.id);
              return (
                <motion.div
                  key={dream.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`glass-card p-4 transition-all duration-200 ${
                    isActive ? 'ring-1 ring-white/20' : ''
                  }`}
                >
                  {/* Clickable area — opens viewer */}
                  <button
                    onClick={() => navigate(`/dream/${dream.id}`)}
                    className="w-full"
                  >
                    <div className="flex gap-4">
                      {dream.thumbnailUrl ? (
                        <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                          <img src={dream.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0 glass-surface">
                          <Sparkles className="w-6 h-6 text-white/15" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0 text-left">
                        <div className="mb-1.5">
                          {dream.status === 'realized' ? (
                            <span className="pill-success">
                              <Check className="w-3 h-3" />
                              Realise
                            </span>
                          ) : isActive ? (
                            <span className="pill-active">
                              <Sparkles className="w-3 h-3" />
                              Actif
                            </span>
                          ) : (
                            <span className="pill-glass">
                              <Clock className="w-3 h-3" />
                              En pause
                            </span>
                          )}
                        </div>

                        <p className="font-medium line-clamp-2 mb-1 text-white/90">
                          {dream.title || dream.description.substring(0, 100)}
                        </p>

                        <p className="text-sm text-ivory-faint">
                          {dream.imagesCount} images
                        </p>
                      </div>

                      <ChevronRight className="w-5 h-5 text-white/15 flex-shrink-0 self-center" />
                    </div>
                  </button>

                  {/* Actions */}
                  {dream.status !== 'realized' && (
                    <div className="flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      {/* Start/Stop toggle */}
                      <button
                        onClick={() => handleToggleActive(dream.id)}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all duration-200 ${
                          isActive ? 'text-white/90' : 'text-white/50'
                        }`}
                        style={{
                          background: isActive
                            ? 'rgba(255,255,255,0.12)'
                            : 'rgba(255,255,255,0.04)',
                          border: isActive
                            ? '1px solid rgba(255,255,255,0.15)'
                            : '1px solid transparent',
                        }}
                      >
                        {isActive ? (
                          <>
                            <Square className="w-3.5 h-3.5" />
                            Arreter
                          </>
                        ) : (
                          <>
                            <Zap className="w-3.5 h-3.5" />
                            Activer
                          </>
                        )}
                      </button>

                      {/* Mark realized */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleMarkRealized(dream.id); }}
                        className="px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
                        style={{ background: 'rgba(34,197,94,0.08)', color: '#6ee7a0', border: '1px solid rgba(34,197,94,0.12)' }}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
